import ErrorResponse from 'src/Models/ErrorResponse';
import { MockServiceSettings } from 'src/App/Mock/Model/MockServiceSettings';
import { UATClient } from 'src/Common/Utilities/UATClient';
import { IKeyValuePair } from 'src/Common/Models/IKeyValuePair';
import { MockSettings } from 'src/App/Mock/MockSettings';
import { HttpVerbs } from 'src/Common/Models/UATConstants';

// ServiceFactory to determine whether to call actual service vs mock service.
export class ServiceFactoryToExecuteApi {
  // reading the settings file directly.
  private readonly mockServiceSettings: MockServiceSettings[] = MockSettings.Settings;

  ExecuteRequest<T>(
    apiUrl: string,
    params: IKeyValuePair[],
    httpMethod: string | HttpVerbs,
    data: any,
    onRequestSuccess: (response: T) => void,
    onRequestFailed: (error: ErrorResponse) => void,
    useMockService: boolean = false,
    beforeRequest?: () => void,
    customRequestTimeOut?: number
  ) {
    if (typeof httpMethod === 'string') {
      httpMethod = HttpVerbs[httpMethod];
    }
    const foundMockSettings: MockServiceSettings | undefined = this.mockServiceSettings.find(
      (x) => x.url === apiUrl && x.httpMethod === httpMethod
    );

    // check if  mock services flag is enabled and there is an entry in the mock settings file for the url requested.
    if (useMockService && foundMockSettings) {
      if (foundMockSettings.responseMethod) {
        // execute method based on the method type
        const responseData = foundMockSettings.responseMethod(params, data);
        // If no method found to execute send empty response.
        onRequestSuccess(responseData);
      } else if (foundMockSettings.responseJsonPath) {
        const responseData = require(foundMockSettings.responseJsonPath);
        // If no method found to execute send empty response.
        onRequestSuccess(responseData);
      }
    } else {
      apiUrl = this.getapiUrl(apiUrl, params);
      // Add additional parameters for doRequest_New if required.
      UATClient.doRequest_New(
        HttpVerbs[httpMethod],
        apiUrl,
        data,
        () => beforeRequest,
        (serviceResponse) => {
          let responseData: T = serviceResponse.data || serviceResponse
          onRequestSuccess(responseData);
        },
        onRequestFailed,
        undefined,
        undefined,
        customRequestTimeOut ? customRequestTimeOut : undefined
      );
    }
  }

  ExecuteRequestNoCache<T>(
    apiUrl: string,
    params: IKeyValuePair[],
    httpMethod: string | HttpVerbs,
    data: any,
    onRequestSuccess: (response: T) => void,
    onRequestFailed: (error: ErrorResponse) => void,
    useMockService: boolean = false,
    beforeRequest?: () => void,
    customRequestTimeOut?: number
  ) {
    if (typeof httpMethod === 'string') {
      httpMethod = HttpVerbs[httpMethod];
    }
    const foundMockSettings: MockServiceSettings | undefined = this.mockServiceSettings.find(
      (x) => x.url === apiUrl && x.httpMethod === httpMethod
    );

    // check if  mock services flag is enabled and there is an entry in the mock settings file for the url requested.
    if (useMockService && foundMockSettings) {
      if (foundMockSettings.responseMethod) {
        // execute method based on the method type
        const responseData = foundMockSettings.responseMethod(params, data);
        // If no method found to execute send empty response.
        onRequestSuccess(responseData);
      } else if (foundMockSettings.responseJsonPath) {
        const responseData = require(foundMockSettings.responseJsonPath);
        // If no method found to execute send empty response.
        onRequestSuccess(responseData);
      }
    } else {
      apiUrl = this.getapiUrl(apiUrl, params);
      // Add additional parameters for doRequest_New if required.
      UATClient.doRequest_New_NoCache(
        HttpVerbs[httpMethod],
        apiUrl,
        data,
        () => beforeRequest,
        (serviceResponse) => {
          const responseData: T = serviceResponse.data;
          onRequestSuccess(responseData);
        },
        onRequestFailed,
        undefined,
        undefined,
        customRequestTimeOut ? customRequestTimeOut : undefined
      );
    }
  }

  async ExecuteRequestAsync<T>(
    apiUrl: string,
    params: IKeyValuePair[],
    httpMethod: string | HttpVerbs,
    data: any,
    beforeRequest?: () => void,
    customRequestTimeOut?: number
  ): Promise<T> {
    if (typeof httpMethod === 'string') {
      httpMethod = HttpVerbs[httpMethod];
    }

    apiUrl = this.getapiUrl(apiUrl, params);
    // Add additional parameters for doRequest_New if required.
    return await UATClient.doRequest_Async(
      HttpVerbs[httpMethod],
      apiUrl,
      data,
      beforeRequest,
      customRequestTimeOut ? customRequestTimeOut : undefined
    );
  }

  ExecuteAdoRequest<T>(
    apiUrl: string,
    params: IKeyValuePair[],
    httpMethod: string | HttpVerbs,
    data: any,
    onRequestSuccess: (response: T) => void,
    onRequestFailed: (error: ErrorResponse) => void,
    useMockService: boolean = false,
    beforeRequest?: () => void,
    customRequestTimeOut?: number,
    isCoreAPI?: boolean
  ) {
    if (typeof httpMethod === 'string') {
      httpMethod = HttpVerbs[httpMethod];
    }
    const foundMockSettings: MockServiceSettings | undefined = this.mockServiceSettings.find(
      (x) => x.url === apiUrl && x.httpMethod === httpMethod
    );

    // check if  mock services flag is enabled and there is an entry in the mock settings file for the url requested.
    if (useMockService && foundMockSettings) {
      if (foundMockSettings.responseMethod) {
        // execute method based on the method type
        const responseData = foundMockSettings.responseMethod(params, data);
        // If no method found to execute send empty response.
        onRequestSuccess(responseData);
      } else if (foundMockSettings.responseJsonPath) {
        const responseData = require(foundMockSettings.responseJsonPath);
        // If no method found to execute send empty response.
        onRequestSuccess(responseData);
      }
    } else {
      apiUrl = this.getapiUrl(apiUrl, params);
      // Add additional parameters for doRequest_New if required.
      UATClient.doAdoRequest_New(
        HttpVerbs[httpMethod],
        apiUrl,
        data,
        () => beforeRequest,
        (serviceResponse) => {
          const responseData: T = serviceResponse.data;
          onRequestSuccess(responseData);
        },
        onRequestFailed,
        undefined,
        undefined,
        customRequestTimeOut ? customRequestTimeOut : undefined,
        isCoreAPI
      );
    }
  }

  // Append api url with params.
  private getapiUrl(apiUrl: string, params: IKeyValuePair[]) {
    let urlWithParams: string = apiUrl;
    if (params && params.length > 0) {
      params.forEach((param) => {
        urlWithParams = urlWithParams.concat('/').concat(param.Value);
      });
    }
    return urlWithParams;
  }
}
export const ServiceFactoryClientExecuteApi = new ServiceFactoryToExecuteApi();
